import React from "react";
import {FaTrashAlt} from "react-icons/fa";
import Swal from "sweetalert2";

// --- ESSENTIAL IMPORTS PULLED FROM CONTEXT ---
import {useGroupContext} from "../../contexts/group_context";
import {useTagContext} from "../../contexts/tag_context";
import {useNavigationContext} from "../../contexts/NavContext/nav-context";
import {withApiErrorHandlerThrow} from "../../error_handling";
import {ThemeColors} from "../../colors";
import {clearGroupWorkspace} from "../../api";
// ---------------------------------------------

/**
 * A simple component to manage high-level workspace actions,
 * primarily focusing on the "Clear Workspace" function.
 */
export const ManageGroupActions: React.FC = () => {
    // We only need 'clearing' state for the button visual feedback
    const [clearing, setClearing] = React.useState(false);

    // Contexts needed for action:
    const {currentGroup, refreshCurrentGroup} = useGroupContext()!;
    const {navigateTo} = useNavigationContext()!;
    // Use reFetchTags/Docs if successful clearing requires a UI refresh
    const {reFetchTags} = useTagContext()!;

    const handleClearWorkspace = async () => {
        const result = await Swal.fire({
            title: "Are you absolutely sure?",
            text: `This action will permanently delete ALL documents, tags, chats, and data in the "${currentGroup!.name}" workspace. This cannot be undone.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: ThemeColors.colorContrastLightHex, // Red confirmation button
            cancelButtonColor: ThemeColors.neutralDarkHex,
            confirmButtonText: "Yes, clear and delete everything!",
        });

        if (result.isConfirmed) {
            setClearing(true);
            // Show loading modal while delete + refresh runs
            void Swal.fire({
                title: "Clearing workspace...",
                text: "Deleting all documents, tags, and data. This may take a moment.",
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => Swal.showLoading(),
            });
            try {
                // CALL CLIENT: Requires only the group_id
                await withApiErrorHandlerThrow(
                    () => clearGroupWorkspace(currentGroup!.id),
                    {titleQualifier: "Error Clearing Workspace", navigateTo: navigateTo}
                );

                // Refresh UI state before showing success so there's no choppy update
                await refreshCurrentGroup();
                await reFetchTags();

                Swal.close();
                await Swal.fire("Cleared!", "The workspace has been completely cleared.", "success");

            } catch (error) {
                Swal.close();
                console.error("Workspace clearance failed:", error);
            } finally {
                setClearing(false);
            }
        }
    };

    return (
        <div className={"flex justify-end items-center"}>

            {/* Clear Workspace Button (Trash Can Icon) */}
            <div
                className={`flex items-center justify-center p-2 m-2 rounded-lg shadow cursor-pointer transition 
                    ${clearing ? 'bg-gray-200' : 'bg-white hover:bg-red-100'} 
                    ${clearing ? 'opacity-50' : ''}`}
                onClick={clearing ? undefined : handleClearWorkspace}
                title={`Clear and delete all data from ${currentGroup!.name} workspace`}
            >
                <FaTrashAlt className="text-red-500 w-[1.5em] h-[1.5em]"/>
            </div>

            {/* Note: The original 'Modify Codes' button has been removed */}
        </div>
    );
};